<template>
  <span :class="chipClasses" :style="{ backgroundColor: colorIsValidHex ? (color as string) : undefined }">
    <slot />
  </span>
</template>

<script lang="ts" setup>
import { MAX_LUMINANCE_FOR_LIGHT_TEXT } from '@lib/constants/color.const'
import { getLuminance } from '@lib/util/color'
import { computed } from 'vue'

defineOptions({ name: 'CChip' })

const props = withDefaults(
  defineProps<{
    color?: string | null
    size?: 'small' | 'medium' | 'large'
    bold?: boolean
    uppercase?: boolean
    variant?: 'flat' | 'tonal' | 'outlined' | 'text' | 'plain'
  }>(),
  {
    color: '#000000',
    size: 'small',
    bold: true,
    uppercase: true,
    variant: 'flat',
  },
)

const isLightText = computed(() => {
  if (!props.color) return true
  const luminance = getLuminance(props.color)

  return luminance < MAX_LUMINANCE_FOR_LIGHT_TEXT
})

const isValidHex = (hex: string) => /^#[0-9A-F]{6}$/i.test(hex)

const colorIsValidHex = computed(() => !!props.color && isValidHex(props.color))

const chipClasses = computed(() => [
  'c-chip',
  'inline-flex items-center rounded-full',
  {
    'color-base': colorIsValidHex.value && isLightText.value,
    'color-text2': colorIsValidHex.value && !isLightText.value,
    'font-bold': props.bold,
    uppercase: props.uppercase,
    'text-xs px-2 py-1': props.size === 'small',
    'text-sm px-3 py-1.5': props.size === 'medium',
    'text-base px-4 py-2': props.size === 'large',
    'bg-opacity-100': props.variant === 'flat',
    'bg-opacity-20': props.variant === 'tonal',
    'border border-solid': props.variant === 'outlined',
    'bg-transparent': props.variant === 'text' || props.variant === 'plain',
  },
])
</script>
